<template>
    <section class="review-detail-book-context rounded-2xl border border-foxy-border/80 bg-foxy-surface/90 p-4 shadow-sm shadow-foxy-cocoa/5 dark:shadow-black/15">
        <div class="flex items-start gap-4">
            <a :href="bookUrl" class="review-detail-book-context__cover">
                <img :src="book.cover" :alt="book.title" data-fallback="bookCover" />
                <book-status-bookmark v-if="hasVisibleStatus" :status="currentStatus" />
            </a>

            <div class="min-w-0 flex-1 pt-0.5">
                <a :href="bookUrl" class="review-detail-book-context__title">{{ book.title }}</a>
                <a v-if="authorUrl" :href="authorUrl" class="review-detail-book-context__author">{{ authorName }}</a>
                <span v-else class="review-detail-book-context__author">{{ authorName }}</span>

                <div v-if="ratingLabel" class="mt-2 flex flex-wrap items-center gap-x-1.5 text-xs text-foxy-text-secondary">
                    <span class="inline-flex items-center gap-1 font-bold text-foxy-text">
                        <i class="fas fa-star text-foxy-honey" aria-hidden="true"></i>
                        {{ ratingLabel }}/10
                    </span>
                    <template v-if="ratingCount !== null">
                        <span aria-hidden="true">·</span>
                        <span>{{ ratingCountLabel }}</span>
                    </template>
                </div>
            </div>
        </div>

        <div class="mt-4 border-t border-foxy-border/70 pt-4">
            <add-to-library-button
                :book="book"
                :is-authenticated="isAuthenticated"
                :initial-status="currentStatus"
                :status-from-server="true"
                variant="review-detail"
                @status-updated="handleStatusUpdated"
                @status-snapshot="handleStatusSnapshot"
                @manage="openManagement"
            />
        </div>

        <a :href="reviewsUrl" class="review-detail-book-context__reviews">Усі рецензії та відгуки →</a>

        <review-reading-management-drawer-host
            v-if="isAuthenticated && viewerProfile"
            ref="readingManagement"
            :profile="viewerProfile"
        />
    </section>
</template>

<script>
import AddToLibraryButton from '../AddToLibraryButton.vue';
import BookStatusBookmark from '../books/BookStatusBookmark.vue';
import ReviewReadingManagementDrawerHost from '../ReviewReadingManagementDrawerHost.vue';
import { formatTenPointRating, ratingCountForDisplay } from '../../utils/bookRatingPresentation';
import { isVisibleBookStatus } from '../../utils/bookStatusPresentation';
import { formatCount } from '../../utils/ukrainianPluralization';

export default {
    name: 'ReviewDetailBookContext',
    components: { AddToLibraryButton, BookStatusBookmark, ReviewReadingManagementDrawerHost },
    props: {
        viewerItem: { type: Object, default: null },
        book: { type: Object, required: true },
        bookUrl: { type: String, required: true },
        reviewsUrl: { type: String, required: true },
        authorName: { type: String, required: true },
        authorUrl: { type: String, default: '' },
        isAuthenticated: { type: Boolean, default: false },
        viewerProfile: { type: Object, default: null },
    },
    data() {
        const context = this.viewerItem?.card_context || {};
        return {
            localStatus: this.viewerItem?.status || null,
            managementStatusId: Number(context.reading_status_id || this.viewerItem?.id || 0),
            managementReadingId: Number(context.reading_action_context?.reading_instance_id || 0) || null,
        };
    },
    computed: {
        currentStatus() {
            return this.localStatus;
        },
        hasVisibleStatus() {
            return isVisibleBookStatus(this.currentStatus);
        },
        ratingLabel() {
            return formatTenPointRating(this.book);
        },
        ratingCount() {
            return this.book.ratings_count === null || this.book.ratings_count === undefined
                ? null
                : ratingCountForDisplay(this.book);
        },
        ratingCountLabel() {
            return formatCount(this.ratingCount, 'оцінка', 'оцінки', 'оцінок');
        },
    },
    methods: {
        handleStatusUpdated(status) {
            this.localStatus = status;
        },
        handleStatusSnapshot(snapshot) {
            const statusId = Number(snapshot?.reading_status_id || snapshot?.id || 0);
            const readingId = Number(snapshot?.open_reading_instance_id || snapshot?.reading_context_instance_id || 0);
            if (statusId > 0) this.managementStatusId = statusId;
            this.managementReadingId = readingId > 0 ? readingId : null;
        },
        openManagement(trigger) {
            if (!this.isAuthenticated || this.managementStatusId <= 0) return;
            this.$refs.readingManagement?.open(
                {
                    reading_status_id: this.managementStatusId,
                    reading_instance_id: this.managementReadingId,
                },
                trigger,
            );
        },
    },
};
</script>

<style scoped>
.review-detail-book-context {
    --book-status-want-fill: #5b458c;
    --book-status-want-glyph: #fffaf7;
    --book-status-want-edge: #fff9f2;
    --book-status-reading-fill: #2e6095;
    --book-status-reading-glyph: #fffaf7;
    --book-status-reading-edge: #fff9f2;
    --book-status-read-fill: #356b4a;
    --book-status-read-glyph: #fffaf7;
    --book-status-read-edge: #fff9f2;
    --book-status-dnf-fill: #9a4b3d;
    --book-status-dnf-glyph: #fffaf7;
    --book-status-dnf-edge: #fff9f2;
}
.review-detail-book-context__cover {
    position: relative;
    display: block;
    width: 5.25rem;
    flex: 0 0 5.25rem;
    overflow: hidden;
    border: 1px solid rgb(var(--foxy-border));
    border-radius: 0.65rem;
    background: rgb(var(--foxy-elevated));
    box-shadow: 0 0.35rem 1rem rgb(var(--foxy-cocoa) / 0.12);
}
.review-detail-book-context__cover img {
    display: block;
    width: 100%;
    aspect-ratio: 2 / 3;
    object-fit: cover;
}
.review-detail-book-context__cover:focus-visible {
    outline: 2px solid rgb(var(--foxy-action));
    outline-offset: 3px;
}
.review-detail-book-context__title,
.review-detail-book-context__author,
.review-detail-book-context__reviews {
    display: block;
}
.review-detail-book-context__title {
    color: rgb(var(--foxy-text));
    font-size: 1rem;
    font-weight: 800;
    line-height: 1.35;
}
.review-detail-book-context__author {
    margin-top: 0.25rem;
    color: rgb(var(--foxy-text-secondary));
    font-size: 0.82rem;
}
a.review-detail-book-context__author:hover,
.review-detail-book-context__title:hover,
.review-detail-book-context__reviews:hover {
    color: rgb(var(--foxy-action));
}
.review-detail-book-context__reviews {
    margin-top: 1rem;
    color: rgb(var(--foxy-action));
    font-size: 0.78rem;
    font-weight: 700;
}
</style>
